/**
 * Основная игровая логика "Алхимическая Дуэль"
 */

import { GameState, Card, GameAction } from '../types/game';
import { DeckGenerator } from './deck';
import { findRecipe } from './recipes';
import { isPhilosopherStone, ELEMENTS } from './elements';

/**
 * Класс управления игрой
 * Детерминированная логика, выполняется на обоих клиентах
 */
export class GameEngine {
  private state: GameState;
  private deckGenerator: DeckGenerator;

  constructor(seed: number, roomId: string) {
    this.deckGenerator = new DeckGenerator(seed);

    // Инициализация начального состояния игры
    this.state = {
      roomId,
      seed,
      cardCounter: 0,
      currentTurn: 1,
      turnNumber: 1,

      player1: {
        hand: [],
        discardedThisTurn: false,
      },

      player2: {
        hand: [],
        discardedThisTurn: false,
      },

      table: [],
      discardPile: [],
      winner: null,
    };

    // Генерируем начальные карты
    // DeckGenerator возвращает только ключи элементов, карты создаем через this.createCard
    // Это гарантирует детерминированность - одинаковый seed → одинаковые элементы

    // Стол: 6 базовых карт
    const tableElements = this.deckGenerator.generateTableElements();
    for (const elementKey of tableElements) {
      this.state.table.push(this.createCard(elementKey));
    }

    // Игрок 1: 3 случайные базовые карты
    const player1Elements = this.deckGenerator.generatePlayer1Elements();
    for (const elementKey of player1Elements) {
      this.state.player1.hand.push(this.createCard(elementKey));
    }

    // Игрок 2: все 4 базовые карты (fire, water, earth, air)
    const player2Elements = this.deckGenerator.generatePlayer2Elements();
    for (const elementKey of player2Elements) {
      this.state.player2.hand.push(this.createCard(elementKey));
    }

  }

  /**
   * Получить текущее состояние игры
   */
  getState(): GameState {
    return JSON.parse(JSON.stringify(this.state)); // Deep copy
  }

  /**
   * Загрузить состояние игры (для синхронизации)
   */
  loadState(state: GameState): void {
    this.state = JSON.parse(JSON.stringify(state)); // Deep copy
    this.deckGenerator = new DeckGenerator(state.seed);
  }

  /**
   * Выполнить действие игрока
   * Детерминированная функция - одинаковый результат на обоих клиентах
   */
  executeAction(playerId: 1 | 2, action: GameAction): boolean {
    // Проверка, что игра не закончена
    if (this.state.winner !== null) {
      console.warn('Game is already over');
      return false;
    }

    // Surrender можно сделать в любой момент
    if (action.type === 'surrender') {
      return this.executeSurrender(playerId);
    }

    // Для остальных действий проверяем, что сейчас ход этого игрока
    if (this.state.currentTurn !== playerId) {
      console.warn(`Not ${playerId}'s turn`);
      return false;
    }

    switch (action.type) {
      case 'combine':
        return this.executeCombine(playerId, action.cardId1, action.cardId2);

      case 'discard':
        return this.executeDiscard(playerId, action.cardIds);

      case 'end_turn':
        return this.executeEndTurn(playerId);

      default:
        console.warn('Unknown action type');
        return false;
    }
  }

  /**
   * Комбинирование двух карт
   */
  private executeCombine(playerId: 1 | 2, cardId1: string, cardId2: string): boolean {
    const player = playerId === 1 ? this.state.player1 : this.state.player2;

    // Найти карты
    const card1 = this.findCard(cardId1);
    const card2 = this.findCard(cardId2);

    if (!card1 || !card2) {
      console.warn(`❌ Card not found: ${cardId1} or ${cardId2}`);
      return false;
    }

    // Проверить источники карт
    const card1Source = this.getCardSource(cardId1);
    const card2Source = this.getCardSource(cardId2);

    if (!card1Source || !card2Source) {
      console.warn('Cannot determine card source');
      return false;
    }

    // Проверить валидность комбинации (правила игры)
    if (!this.canCombine(card1Source, card2Source, player.hand.length)) {
      console.warn('Invalid combination according to game rules');
      return false;
    }

    // Найти рецепт
    const resultElement = findRecipe(card1.element, card2.element);

    if (!resultElement) {
      console.warn(`No recipe found for: ${card1.element} + ${card2.element}`);
      return false;
    }

    // Удалить использованные карты
    this.removeCard(cardId1);
    this.removeCard(cardId2);

    // Добавить использованные карты в сброс
    this.state.discardPile.push(card1, card2);

    // Создать новую карту
    const newCard = this.createCard(resultElement);
    player.hand.push(newCard);

    // Проверить победу
    if (isPhilosopherStone(resultElement)) {
      this.state.winner = playerId;
    }

    return true;
  }

  /**
   * Сброс карт
   */
    private executeDiscard(playerId: 1 | 2, cardIds: string[]): boolean {
        const player = playerId === 1 ? this.state.player1 : this.state.player2;

        // Уже сбрасывал в этом ходу
        if (player.discardedThisTurn) {
            console.warn('Already discarded this turn');
            return false;
        }

        // Лимит: 1–4 карты
        if (cardIds.length < 1 || cardIds.length > 4) {
            console.warn('Can only discard 1-4 cards');
            return false;
        }

        // Проверка валидности карт
        for (const cardId of cardIds) {
            const inHand = player.hand.some(c => c.id === cardId);
            const onTable = this.state.table.some(c => c.id === cardId);

            if (!inHand && !onTable) {
                console.warn(`Card ${cardId} is not discardable`);
                return false;
            }
        }

        // Сброс карт
        for (const cardId of cardIds) {
            // Из руки
            let index = player.hand.findIndex(c => c.id === cardId);
            if (index !== -1) {
                const [card] = player.hand.splice(index, 1);
                this.state.discardPile.push(card);
                continue;
            }

            // Со стола
            index = this.state.table.findIndex(c => c.id === cardId);
            if (index !== -1) {
                const [card] = this.state.table.splice(index, 1);
                this.state.discardPile.push(card);
                continue;
            }
        }

        player.discardedThisTurn = true;
        return true;
    }

  /**
   * Сдаться
   */
  private executeSurrender(playerId: 1 | 2): boolean {
    // Игрок сдается - противник побеждает
    this.state.winner = playerId === 1 ? 2 : 1;
    return true;
  }

  /**
   * Завершение хода
   */
  private executeEndTurn(playerId: 1 | 2): boolean {
    const player = playerId === 1 ? this.state.player1 : this.state.player2;

    // Добрать карты до 4 в руке
    const cardsNeeded = 4 - player.hand.length;
    for (let i = 0; i < cardsNeeded; i++) {
      const elementKey = this.deckGenerator.getElementForTurn(this.state.turnNumber);
      player.hand.push(this.createCard(elementKey));
    }

    // Сбросить все карты со стола
    //this.state.discardPile.push(...this.state.table);
    //this.state.table = [];

    // Сгенерировать 6 новых карт на стол

      for (let i = this.state.table.length; i < 6; i++) {
        const elementKey = this.deckGenerator.getElementForTurn(this.state.turnNumber);
        this.state.table.push(this.createCard(elementKey));
    }

    // Сбросить флаг сброса
    player.discardedThisTurn = false;

    // Передать ход
    this.state.currentTurn = this.state.currentTurn === 1 ? 2 : 1;

    // Увеличить номер хода (каждые два игрока)
    if (this.state.currentTurn === 1) {
      this.state.turnNumber++;
    }

    return true;
  }

  /**
   * Проверить, можно ли комбинировать карты
   */
  private canCombine(
    source1: 'hand' | 'table',
    source2: 'hand' | 'table',
    handSize: number
  ): boolean {
    // РУКА + РУКА: всегда можно
    if (source1 === 'hand' && source2 === 'hand') {
      return true;
    }

    // РУКА + СТОЛ: всегда можно
    if (source1 !== source2) {
      return true;
    }

    // СТОЛ + СТОЛ: только если в руке < 4 карт (ВСЕГДА)
    if (source1 === 'table' && source2 === 'table') {
      return handSize < 6;
    }

    return false;
  }

  /**
   * Найти карту по ID
   */
  private findCard(cardId: string): Card | null {
    // Поиск в руках игроков
    let card = this.state.player1.hand.find(c => c.id === cardId);
    if (card) return card;

    card = this.state.player2.hand.find(c => c.id === cardId);
    if (card) return card;

    // Поиск на столе
    card = this.state.table.find(c => c.id === cardId);
    if (card) return card;

    return null;
  }

  /**
   * Определить источник карты
   */
  private getCardSource(cardId: string): 'hand' | 'table' | null {
    const currentPlayer = this.state.currentTurn === 1 ? this.state.player1 : this.state.player2;

    if (currentPlayer.hand.find(c => c.id === cardId)) {
      return 'hand';
    }

    if (this.state.table.find(c => c.id === cardId)) {
      return 'table';
    }

    return null;
  }

  /**
   * Удалить карту из игры
   */
  private removeCard(cardId: string): void {
    // Удалить из рук игроков
    let index = this.state.player1.hand.findIndex(c => c.id === cardId);
    if (index !== -1) {
      this.state.player1.hand.splice(index, 1);
      return;
    }

    index = this.state.player2.hand.findIndex(c => c.id === cardId);
    if (index !== -1) {
      this.state.player2.hand.splice(index, 1);
      return;
    }

    // Удалить со стола
    index = this.state.table.findIndex(c => c.id === cardId);
    if (index !== -1) {
      this.state.table.splice(index, 1);
      return;
    }
  }

  /**
   * Создать новую карту с элементом
   */
  private createCard(elementKey: string): Card {
    const element = ELEMENTS[elementKey];
    if (!element) {
      throw new Error(`Unknown element: ${elementKey}`);
    }

    const id = `card_${this.state.cardCounter++}`;
    return {
      id,
      element: elementKey,
      name: element.n,
      icon: element.i,
      level: element.l,
    };
  }
}
